import { cn, ghostAccentIconTileClass } from '@up2stay/up2promo-ui';
import { MapPin } from 'lucide-react';

import type { LocationAddressDetails } from '@/core/components/locations/location-details-form';
import { LocationMapPreview } from '@/core/components/locations/location-map-preview';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupRow,
} from '@/core/components/settings/settings-group';
import { useDictionary } from '@/core/providers/dictionary-provider';

interface LocationReviewSummaryProps {
  name: string;
  address: string;
  latitude: number | undefined;
  longitude: number | undefined;
  googlePlaceId: string | undefined;
  addressDetails?: LocationAddressDetails | null;
  /** Manual create path — no Place ID, quieter no-map treatment. */
  isManual?: boolean;
}

export function LocationReviewSummary({
  name,
  address,
  latitude,
  longitude,
  googlePlaceId,
  isManual = false,
}: LocationReviewSummaryProps) {
  const dict = useDictionary();
  const d = dict.locations?.create?.wizard?.reviewStep;

  const hasCoordinates =
    latitude !== undefined &&
    longitude !== undefined &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude);

  const displayAddress = address.trim() || d?.noAddress;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <SettingsGroup>
          <SettingsGroupRow>
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  ghostAccentIconTileClass,
                  'mt-0.5 flex size-9 shrink-0 items-center justify-center',
                )}
                aria-hidden
              >
                <MapPin className="size-4" />
              </div>
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <p className="truncate text-[15.5px] font-bold leading-tight text-foreground">
                  {name || '—'}
                </p>
                <p className="text-[13.5px] leading-relaxed text-muted-foreground text-pretty">
                  <span className="sr-only">{d?.address}: </span>
                  {displayAddress}
                </p>
                {googlePlaceId && !isManual ? (
                  <p className="truncate font-mono text-[11px] font-semibold uppercase tracking-normal text-muted-foreground">
                    <span className="font-sans font-semibold uppercase tracking-[0.6px]">
                      {d?.googlePlaceId}
                    </span>
                    {': '}
                    <span className="font-normal normal-case">
                      {googlePlaceId}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </SettingsGroupRow>
        </SettingsGroup>
        {isManual && d?.googleLaterHint ? (
          <SettingsGroupFooter>{d.googleLaterHint}</SettingsGroupFooter>
        ) : null}
      </div>

      {hasCoordinates ? (
        <LocationMapPreview
          latitude={latitude}
          longitude={longitude}
          name={name || undefined}
          mapPreviewLabel={d?.mapPreview}
          noCoordinatesLabel={d?.noCoordinates}
          mapUnavailableLabel={d?.mapUnavailable}
          compact
        />
      ) : isManual ? null : (
        <div className="flex flex-col gap-2">
          <SettingsGroup>
            <SettingsGroupRow>
              <div className="flex flex-col items-center justify-center gap-2 py-4">
                <MapPin className="size-8 text-muted-foreground" aria-hidden />
              </div>
            </SettingsGroupRow>
          </SettingsGroup>
          <SettingsGroupFooter>{d?.noCoordinates}</SettingsGroupFooter>
        </div>
      )}
    </div>
  );
}
